import {
  fromUnixTime,
  format,
  formatISO,
  parseISO,
  startOfYesterday,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth
} from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import momenttz from 'moment-timezone';
import { DATE_RANGE } from '@server/constants';

export const getDateRange = (
  str,
  date = new Date(),
  timeZone = process.env.SERVER_TIMEZONE || 'America/New_York'
) => {
  const zonedToday = utcToZonedTime(date, timeZone);

  switch (str) {
    case DATE_RANGE.today:
      return {
        startDate: startOfDay(zonedToday),
        endDate: endOfDay(zonedToday)
      };
    case DATE_RANGE.week:
      return {
        startDate: startOfWeek(zonedToday),
        endDate: endOfWeek(zonedToday)
      };
    case DATE_RANGE.prevWeek: {
      const firstDateOfWeek = startOfWeek(zonedToday);
      const midDateOfPrevWeek = firstDateOfWeek.setDate(
        firstDateOfWeek.getDate() - 3
      );

      return {
        startDate: startOfWeek(midDateOfPrevWeek),
        endDate: endOfWeek(midDateOfPrevWeek)
      };
    }
    case DATE_RANGE.month:
      return {
        startDate: startOfMonth(zonedToday),
        endDate: endOfMonth(zonedToday)
      };
    case DATE_RANGE.pastDue:
      return {
        startDate: null,
        endDate: startOfYesterday()
      };
    case DATE_RANGE.all:
      return null;
    default:
      return null;
  }
};

export const getDayOfWeek = (date = new Date()) => format(date, 'iiii');

export const getHour = (date = new Date()) => format(date, 'HH');

export const getMinute = (date = new Date()) => format(date, 'mm');

export const getYearFromDateString = (date) => format(parseISO(date), 'yyyy');

export const getMonthFromDateString = (date) => format(parseISO(date), 'MMMM');

export const getStartOfMonthFromDateString = (date) =>
  startOfMonth(parseISO(date));

export const getEndOfMonthFromDateString = (date) => endOfMonth(parseISO(date));

export const getDateStringFromString = (date) =>
  formatISO(parseISO(date), { representation: 'date' });

export const getDateStringFromDateObject = (date) =>
  date.toISOString().substr(0, 10);

export const getDateTimeStringFromTimestamp = (timestamp) =>
  fromUnixTime(timestamp).toISOString();

export const formatDate = (
  date = new Date(),
  dateFormat = 'MM/dd/yyyy HH:mm:ss'
) => {
  const timeZone = momenttz.tz.guess();
  const zonedDate = utcToZonedTime(date, timeZone);
  return format(zonedDate, [dateFormat], { timeZone });
};

export const formatEST = (
  date = new Date(),
  dateFormat = 'MM/dd/yyyy HH:mm:ss'
) => {
  const timeZone = 'America/New_York';
  const zonedDate = utcToZonedTime(date, timeZone);
  return format(zonedDate, [dateFormat], { timeZone });
};

export const formatCST = (
  date = new Date(),
  dateFormat = 'MM/dd/yyyy HH:mm:ss'
) => {
  const timeZone = 'America/Chicago';
  const zonedDate = utcToZonedTime(date, timeZone);
  return format(zonedDate, [dateFormat], { timeZone });
};

export const formatMST = (
  date = new Date(),
  dateFormat = 'MM/dd/yyyy HH:mm:ss'
) => {
  const timeZone = 'America/Denver';
  const zonedDate = utcToZonedTime(date, timeZone);
  return format(zonedDate, [dateFormat], { timeZone });
};

export const formatPST = (
  date = new Date(),
  dateFormat = 'MM/dd/yyyy HH:mm:ss'
) => {
  const timeZone = 'America/Los_Angeles';
  const zonedDate = utcToZonedTime(date, timeZone);
  return format(zonedDate, [dateFormat], { timeZone });
};

export const formatTimestamp = (
  timestamp,
  dateFormat = 'MM/dd/yyyy HH:mm:ss'
) => formatDate(fromUnixTime(timestamp), dateFormat);

export const formatString = (date, dateFormat = 'MM/dd/yyyy HH:mm:ss') =>
  formatDate(parseISO(date), dateFormat);

export const getGMTOffsetFromTimezone = (timezone) => {
  const isDST = momenttz().isDST();

  if (isDST && timezone === 'EST') return 'GMT-4';
  if (!isDST && timezone === 'EST') return 'GMT-5';

  if (isDST && timezone === 'CST') return 'GMT-5';
  if (!isDST && timezone === 'CST') return 'GMT-6';

  if (isDST && timezone === 'MT') return 'GMT-6';
  if (!isDST && timezone === 'MT') return 'GMT-7';

  if (isDST && timezone === 'PST') return 'GMT-7';
  if (!isDST && timezone === 'PST') return 'GMT-8';

  return null;
};

/**
 *
 * @param {string | Date} from YYYY-MM-DDTHH:mm:ss.SSSZ | Date format
 * @param {string | Date} to YYYY-MM-DDTHH:mm:ss.SSSZ | Date format
 * @returns {number} Get difference hours in a range.
 */
export const getDifferenceInHours = (from, to) => {
  const timestampFrom = new Date(from).getTime();
  const timestampTo = new Date(to).getTime();

  return (
    Math.round(((timestampTo - timestampFrom) / 1000 / (60 * 60)) * 100) / 100
  );
};

/**
 * @param {Date} date
 * @param {number} offset value of offset from now as seconds
 * @returns {Date}
 */
export const subtractTime = (date = new Date(), offset) => {
  const dateTimestamp = date.getTime();
  return new Date(dateTimestamp - offset * 1000);
};

export function convertNowToYYYYMMDD() {
  const date = new Date();
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
  const day = date.getDate().toString().padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export function getSomeDaysLaterYYYYMMDD(someDays) {
  const today = new Date();
  today.setDate(today.getDate() + someDays);

  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0'); // Adding 1 because getMonth() returns 0-indexed month
  const day = String(today.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}